<template>
  <div class="file-upload-component">
    <!-- 上传限制信息 -->
    <div v-if="showLimitInfo" class="upload-limit-info">
      <span class="limit-text" v-if="acceptTypeText">
        <i class="el-icon-info"></i>
        支持格式: {{ acceptTypeText }}
      </span>
      <span class="limit-text" v-if="maxFileSize">
        <i class="el-icon-warning"></i>
        单文件限制: {{ formatFileSize(maxFileSize) }}
      </span>
    </div>

    <!-- 上传触发区域 - 按钮样式 -->
    <div v-if="uploadType === 'button' && !isUploadDisabled" class="upload-trigger-button">
      <el-upload
        ref="uploadRef"
        class="upload-input-hidden"
        action="#"
        :multiple="multiple"
        :accept="acceptType"
        :on-change="handleFileChange"
        :on-exceed="handleExceed"
        :auto-upload="false"
        :file-list="fileList"
        :before-upload="beforeUpload"
        :drag="false"
      >
        <slot name="upload-btn">
          <el-button type="primary" @click.stop="triggerUpload">
            <i class="el-icon-upload"></i>
            {{ uploadBtnText }}
          </el-button>
        </slot>
      </el-upload>
    </div>

    <!-- 上传触发区域 - 卡片样式 -->
    <div v-else-if="uploadType === 'card' && !isUploadDisabled" class="upload-trigger-card">
      <el-upload
        ref="uploadRef"
        class="upload-card-area"
        action="#"
        :multiple="multiple"
        :accept="acceptType"
        :on-change="handleFileChange"
        :on-exceed="handleExceed"
        :auto-upload="false"
        :file-list="fileList"
        :before-upload="beforeUpload"
        :show-file-list="false"
        drag
      >
        <div class="upload-card-content">
          <i class="el-icon-plus upload-card-icon"></i>
          <div class="upload-card-text">{{ uploadBtnText }}</div>
          <div class="upload-card-hint" v-if="acceptTypeText">支持 {{ acceptTypeText }}</div>
        </div>
      </el-upload>
    </div>

    <!-- 超出数量时的提示 -->
    <div v-if="isUploadDisabled" class="upload-limit-tips">
      <el-button type="info" disabled>
        <i class="el-icon-warning"></i>
        已达到上限
      </el-button>
      <span class="tips-text">最多 {{ realLimit }} 个</span>
    </div>

    <!-- 已上传文件展示区域 -->
    <div class="uploaded-files-container" v-if="allFiles.length > 0">
      <div class="files-header">
        <div class="files-title">
          <i class="el-icon-document"></i>
          已上传文件 ({{ allFiles.length }})
        </div>
        <el-button type="text" size="mini" @click="clearAllFiles" v-if="allowClear">
          <i class="el-icon-delete"></i>
          清空
        </el-button>
      </div>

      <!-- 图片类型 -->
      <div class="file-group" v-if="getFileByType('image').length > 0">
        <div class="file-group-title">
          <i class="el-icon-picture"></i>
          图片 ({{ getFileByType('image').length }})
        </div>
        <div class="file-items image-items">
          <div
            class="file-item image-item"
            v-for="file in getFileByType('image')"
            :key="file.id"
          >
            <div class="file-thumbnail" @click="previewFile(file)">
              <img :src="file.url" alt="图片预览" class="image-preview" />
              <div class="file-overlay">
                <i class="el-icon-zoom-in"></i>
              </div>
            </div>
            <div class="file-info">
              <el-tooltip :content="file.name" placement="top" :open-delay="300">
                <div class="file-name">{{ file.name }}</div>
              </el-tooltip>
              <div class="file-actions">
                <el-button type="text" size="mini" @click="previewFile(file)" title="预览">
                  <i class="el-icon-view"></i>
                </el-button>
                <el-button type="text" size="mini" @click="downloadFile(file)" title="下载">
                  <i class="el-icon-download"></i>
                </el-button>
                <el-button type="text" size="mini" class="text-danger" @click="removeFile(file)" title="删除">
                  <i class="el-icon-delete"></i>
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 视频类型 -->
      <div class="file-group" v-if="getFileByType('video').length > 0">
        <div class="file-group-title">
          <i class="el-icon-video-camera"></i>
          视频 ({{ getFileByType('video').length }})
        </div>
        <div class="file-items video-items">
          <div
            class="file-item video-item"
            v-for="file in getFileByType('video')"
            :key="file.id"
          >
            <div class="file-thumbnail video-thumbnail" @click="previewFile(file)">
              <!-- 视频封面：优先显示视频首帧，没有则显示默认封面 -->
              <div class="video-cover" v-if="!file.coverUrl">
                <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 1024 1024'%3E%3Cpath fill='%23ffffff' d='M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372 0-205.4 166.6-372 372-372s372 166.6 372 372c0 205.4-166.6 372-372 372z'/%3E%3Cpath fill='%23ffffff' d='M448 336a48 48 0 1 0-96 0 48 48 0 1 0 96 0zm-48 304c0 26.5 21.5 48 48 48h32c26.5 0 48-21.5 48-48v-64c0-26.5-21.5-48-48-48H448c-26.5 0-48 21.5-48 48v64z'/%3E%3C/svg%3E" alt="视频封面" class="video-default-cover" />
                <i class="el-icon-video-play video-play-icon"></i>
              </div>
              <video
                v-else
                class="video-preview"
                preload="metadata"
                :poster="file.coverUrl"
              >
                <source :src="file.url" :type="file.mime_type" />
              </video>
              <div class="video-duration" v-if="file.duration">{{ formatDuration(file.duration) }}</div>
              <div class="file-overlay play-overlay">
                <i class="el-icon-video-play"></i>
              </div>
            </div>
            <div class="file-info">
              <el-tooltip :content="file.name" placement="top" :open-delay="300">
                <div class="file-name">{{ file.name }}</div>
              </el-tooltip>
              <div class="file-size">{{ file.size ? formatFileSize(file.size) : '' }}</div>
              <div class="file-actions">
                <el-button type="text" size="mini" @click="previewFile(file)" title="播放">
                  <i class="el-icon-video-play"></i>
                </el-button>
                <el-button type="text" size="mini" @click="downloadFile(file)" title="下载">
                  <i class="el-icon-download"></i>
                </el-button>
                <el-button type="text" size="mini" class="text-danger" @click="removeFile(file)" title="删除">
                  <i class="el-icon-delete"></i>
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 音频类型 -->
      <div class="file-group" v-if="getFileByType('audio').length > 0">
        <div class="file-group-title">
          <i class="el-icon-headset"></i>
          音频 ({{ getFileByType('audio').length }})
        </div>
        <div class="file-items audio-items">
          <div
            class="file-item audio-item"
            v-for="file in getFileByType('audio')"
            :key="file.id"
          >
            <div class="audio-icon" @click="previewFile(file)">
              <i class="el-icon-headset"></i>
            </div>
            <div class="file-info">
              <el-tooltip :content="file.name" placement="top" :open-delay="300">
                <div class="file-name">{{ file.name }}</div>
              </el-tooltip>
              <div class="file-size">{{ file.size ? formatFileSize(file.size) : '' }}</div>
              <div class="file-actions">
                <el-button type="text" size="mini" @click="previewFile(file)" title="播放">
                  <i class="el-icon-video-play"></i>
                </el-button>
                <el-button type="text" size="mini" @click="downloadFile(file)" title="下载">
                  <i class="el-icon-download"></i>
                </el-button>
                <el-button type="text" size="mini" class="text-danger" @click="removeFile(file)" title="删除">
                  <i class="el-icon-delete"></i>
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 文档类型 -->
      <div class="file-group" v-if="getFileByType('document').length > 0">
        <div class="file-group-title">
          <i class="el-icon-document"></i>
          文档 ({{ getFileByType('document').length }})
        </div>
        <div class="file-items document-items">
          <div
            class="file-item document-item"
            v-for="file in getFileByType('document')"
            :key="file.id"
          >
            <div class="document-icon" :class="getDocTypeClass(file)" @click="previewFile(file)">
              <template v-if="file.extension === 'pdf'">
                <i class="el-icon-document" style="color: #f56c6c;"></i>
              </template>
              <template v-else-if="['doc', 'docx'].includes(file.extension)">
                <i class="el-icon-document" style="color: #409eff;"></i>
              </template>
              <template v-else-if="['xls', 'xlsx'].includes(file.extension)">
                <i class="el-icon-document" style="color: #67c23a;"></i>
              </template>
              <template v-else-if="['ppt', 'pptx'].includes(file.extension)">
                <i class="el-icon-document" style="color: #e6a23c;"></i>
              </template>
              <template v-else-if="['txt'].includes(file.extension)">
                <i class="el-icon-document" style="color: #909399;"></i>
              </template>
              <template v-else-if="['zip', 'rar', '7z'].includes(file.extension)">
                <i class="el-icon-document" style="color: #8e44ad;"></i>
              </template>
              <template v-else>
                <i class="el-icon-document"></i>
              </template>
            </div>
            <div class="file-info">
              <el-tooltip :content="file.name" placement="top" :open-delay="300">
                <div class="file-name">{{ file.name }}</div>
              </el-tooltip>
              <div class="file-size">{{ file.size ? formatFileSize(file.size) : '' }}</div>
              <div class="file-actions">
                <el-button type="text" size="mini" @click="previewFile(file)" title="预览">
                  <i class="el-icon-view"></i>
                </el-button>
                <el-button type="text" size="mini" @click="downloadFile(file)" title="下载">
                  <i class="el-icon-download"></i>
                </el-button>
                <el-button type="text" size="mini" class="text-danger" @click="removeFile(file)" title="删除">
                  <i class="el-icon-delete"></i>
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <el-dialog
      title="图片预览"
      :visible.sync="imagePreviewVisible"
      width="80%"
      top="5vh"
      append-to-body
      class="image-preview-dialog"
    >
      <img v-if="previewImageUrl" :src="previewImageUrl" class="preview-image" />
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import COS from 'cos-js-sdk-v5'
import { getPolicyTxCloudAiCourse } from '../../api/api'
import { Loading } from 'element-ui'

export default {
  name: 'uploadOnly',
  props: {
    // 是否支持多选
    multiple: { type: Boolean, default: false },
    // 上传触发类型: button(按钮) | card(卡片)
    uploadType: { type: String, default: 'button' },
    // 接受的文件类型
    acceptType: { type: String, default: '' },
    // 自定义请求头
    llmHeaders: { type: Object, default: () => ({}) },
    // 文件数量限制
    limit: {
      type: Number,
      default: 1
    },
    // 历史文件
    historyFiles: { type: Array, default: () => [] },
    // 上传按钮文字
    uploadBtnText: { type: String, default: '选择文件' },
    // 是否显示限制信息
    showLimitInfo: { type: Boolean, default: true },
    // 是否允许清空
    allowClear: { type: Boolean, default: true },
    // 最大文件大小（字节）
    maxFileSize: { type: Number, default: 0 }
  },
  data() {
    return {
      fileList: [],
      loadings: null,
      cosInstance: null,
      allFiles: [],
      imagePreviewVisible: false,
      previewImageUrl: ''
    }
  },
  computed: {
    // 计算实际生效的limit
    realLimit() {
      return this.limit
    },
    // 判断是否禁用上传
    isUploadDisabled() {
      return this.allFiles.length >= this.realLimit
    },
    // 文件类型显示文本
    acceptTypeText() {
      if (!this.acceptType) return ''
      const typeMap = {
        'image/*': '图片 (jpg, jpeg, png, gif, webp)',
        'video/*': '视频 (mp4, avi, mov)',
        'audio/*': '音频 (mp3, wav)',
        'application/pdf': 'PDF文档'
      }
      return typeMap[this.acceptType] || this.acceptType.split(',').map(t => {
        return t.trim().replace('.', '').toUpperCase()
      }).join(', ')
    }
  },
  watch: {
    historyFiles: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.allFiles = this.uniqueFiles([...(newVal || [])])
      }
    }
  },
  methods: {
    // 按文件类型筛选
    getFileByType(type) {
      return this.allFiles.filter(file => file.type === type)
    },

    // 获取文档类型样式类
    getDocTypeClass(file) {
      const ext = file.extension || ''
      if (['pdf'].includes(ext)) return 'doc-pdf'
      if (['doc', 'docx'].includes(ext)) return 'doc-word'
      if (['xls', 'xlsx'].includes(ext)) return 'doc-excel'
      if (['ppt', 'pptx'].includes(ext)) return 'doc-ppt'
      return ''
    },

    // 格式化文件大小
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '未知'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    // 格式化时长
    formatDuration(seconds) {
      if (!seconds) return ''
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },

    // 清空所有文件
    clearAllFiles() {
      this.$confirm('确定要清空所有已上传的文件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.allFiles = []
        this.$emit('update:historyFiles', [])
        this.$emit('upload-success', [])
        this.$message.success('已清空所有文件')
      }).catch(() => {})
    },

    // 预览文件
    previewFile(file) {
      try {
        if (file.type === 'image') {
          this.previewImageUrl = file.url
          this.imagePreviewVisible = true
        } else if (file.type === 'video') {
          window.open(file.url, '_blank')
        } else if (file.type === 'audio') {
          const audioWindow = window.open('', '_blank')
          audioWindow.document.write(`
            <html>
              <head><title>播放音频 - ${file.name}</title></head>
              <body style="display:flex;justify-content:center;align-items:center;height:100vh;margin:0;background:#f5f5f5;">
                <div style="text-align:center;">
                  <h3 style="color:#333;margin-bottom:20px;">${file.name}</h3>
                  <audio controls autoplay style="width:400px;">
                    <source src="${file.url}" type="${file.mime_type}">
                    您的浏览器不支持音频播放
                  </audio>
                </div>
              </body>
            </html>
          `)
        } else {
          window.open(file.url, '_blank')
        }
      } catch (error) {
        this.$message.error(`预览失败：${error.message}`)
      }
    },

    // 下载文件（新标签页打开）
    downloadFile(file) {
      try {
        const downloadWindow = window.open('', '_blank')
        if (downloadWindow) {
          downloadWindow.location.href = file.url
          this.$message.success(`开始下载文件：${file.name}`)
        } else {
          const link = document.createElement('a')
          link.href = file.url
          link.download = file.name
          link.target = '_blank'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          this.$message.success(`开始下载文件：${file.name}`)
        }
      } catch (error) {
        this.$message.error(`下载文件失败：${error.message}`)
      }
    },

    // 文件删除
    removeFile(file) {
      this.$confirm('确定要删除该文件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.allFiles = this.allFiles.filter(item => item.id !== file.id)
        this.$emit('update:historyFiles', [...this.allFiles])
        this.$emit('upload-success', [...this.allFiles])
        this.$message.success('文件删除成功')
      }).catch(() => {
        this.$message.info('已取消删除')
      })
    },

    // 全局文件去重
    uniqueFiles(fileList) {
      if (!Array.isArray(fileList)) return [];
      
      const map = new Map()
      fileList.forEach(file => {
        const key = file.url || `${file.name}_${file.raw?.size || file.size}`
        if (!map.has(key)) {
          map.set(key, file)
        }
      })
      return Array.from(map.values())
    },

    // 生成唯一ID
    generateUniqueId() {
      return `${Date.now()}_${performance.now()}_${Math.random().toString(36).substr(2, 9)}`
    },

    // 重置上传状态
    resetUploadState() {
      if (this.loadings) {
        this.loadings.close()
        this.loadings = null
      }
      this.fileList = []
      this.cosInstance = null
      const fileInput = this.$refs.uploadRef?.$el.querySelector('input[type="file"]')
      if (fileInput) fileInput.value = ''
    },

    // 触发上传
    triggerUpload(event) {
      if (this.isUploadDisabled) {
        this.$message.warning(`最多只能上传 ${this.realLimit} 个文件`)
        return;
      }
      event?.stopPropagation?.()
      this.$refs.uploadRef?.$el.querySelector('input[type="file"]')?.click()
    },

    // 单个文件重复校验
    checkFileDuplicate(file) {
      return this.allFiles.some(item => {
        if (item.url) {
          return item.url === (file.url || '') || (item.name === file.name && item.raw?.size === file.raw?.size)
        }
        return item.name === file.name && item.raw?.size === file.raw?.size
      })
    },

    // 文件上传前校验（类型+大小+数量）
    beforeUpload(file) {
      // 1. 校验文件类型
      if (this.acceptType) {
        const acceptTypes = this.acceptType.split(',').map(type => type.trim())
        const fileExt = file.name.split('.').pop()?.toLowerCase() || ''
        const fileMime = file.type || ''
        const isTypeMatch = acceptTypes.some(acceptType => {
          if (acceptType.endsWith('*')) {
            const typePrefix = acceptType.replace('*', '')
            return fileMime.startsWith(typePrefix)
          } else if (acceptType.startsWith('.')) {
            return fileExt === acceptType.slice(1)
          } else {
            return fileMime === acceptType || fileExt === acceptType
          }
        })
        if (!isTypeMatch) {
          this.$message.error(`文件类型不支持，请选择${this.acceptTypeText}类型的文件`)
          return false
        }
      }

      // 2. 校验文件大小
      if (this.maxFileSize > 0 && file.size > this.maxFileSize) {
        this.$message.error(`文件大小超出限制，最大支持${this.formatFileSize(this.maxFileSize)}`)
        return false
      }

      // 3. 校验数量限制
      if (this.allFiles.length >= this.realLimit) {
        this.$message.warning(`最多只能上传 ${this.realLimit} 个文件`)
        return false
      }

      return true
    },

    // 文件变更处理
    handleFileChange(file, fileList) {
      if (file.status !== 'ready') return

      // 1. 过滤重复文件
      let filteredList = fileList.filter(item => !this.checkFileDuplicate(item));
      
      // 2. 强制数量限制
      if (filteredList.length > this.realLimit) {
        filteredList = this.multiple ? filteredList.slice(0, this.realLimit) : filteredList.slice(-1);
        this.$message.warning(`最多只能上传 ${this.realLimit} 个文件，已自动截取符合要求的文件`);
      }

      this.fileList = filteredList
      this.handleBatchUpload()
    },

    // 批量上传
    async handleBatchUpload() {
      if (this.fileList.length === 0) return

      // 立即显示 loading
      this.loadings = Loading.service({
        lock: true,
        text: '准备上传中...',
        spinner: "el-icon-loading",
        background: "rgba(0, 0, 0, 0.7)",
        customClass: "upload-loading-mask"
      })

      const newUploadedFiles = []
      const extTypeMap = {
        image: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico'],
        video: ['mp4', 'avi', 'mov', 'flv', 'wmv', 'mkv', 'webm', 'mpeg', '3gp', 'rmvb'],
        audio: ['mp3', 'wav', 'ogg', 'flac', 'aac', 'wma', 'm4a'],
        document: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'zip', 'rar', '7z', 'gz', 'tar']
      }

      for (let i = 0; i < this.fileList.length; i++) {
        const fileItem = this.fileList[i]

        if (this.checkFileDuplicate(fileItem)) {
          this.$message.warning(`文件【${fileItem.name}】已上传，跳过`)
          continue
        }

        // 更新 loading 文字（关闭后重新创建）
        const updateLoadingText = (text) => {
          if (this.loadings) {
            this.loadings.close()
          }
          this.loadings = Loading.service({
            lock: true,
            text: text,
            spinner: "el-icon-loading",
            background: "rgba(0, 0, 0, 0.7)",
            customClass: "upload-loading-mask"
          })
        }

        try {
          updateLoadingText(`正在上传 ${i + 1}/${this.fileList.length} 个文件...`)
          const cosResult = await this.TenXunCloudUploadFile(fileItem.raw, (percent) => {
            // 更新进度
            const text = `上传中 ${(percent * 100).toFixed(1)}%`
            // 避免过于频繁更新，每10%或完成时更新
            if (percent === 1 || Math.floor(percent * 10) !== Math.floor((percent - 0.05) * 10)) {
              updateLoadingText(text)
            }
          })
          const cloudUrl = cosResult?.fileurl
          if (!cloudUrl) {
            this.$message.error(`文件 ${fileItem.name} 上传失败：未获取到云存储地址`)
            continue
          }

          const urlDuplicate = this.allFiles.some(item => item.url === cloudUrl)
          if (urlDuplicate) {
            this.$message.warning(`文件【${fileItem.name}】已上传到云端，跳过`)
            continue
          }

          const fileId = this.generateUniqueId()
          const fileName = fileItem.name
          const extension = fileName.split('.').pop()?.toLowerCase() || ''
          const mime_type = fileItem.raw?.type || ''
          let type = 'document'

          if (extTypeMap.image.includes(extension)) {
            type = 'image'
          } else if (extTypeMap.video.includes(extension)) {
            type = 'video'
          } else if (extTypeMap.audio.includes(extension)) {
            type = 'audio'
          } else if (extTypeMap.document.includes(extension)) {
            type = 'document'
          } else if (mime_type.startsWith('image/')) {
            type = 'image'
          } else if (mime_type.startsWith('video/')) {
            type = 'video'
          } else if (mime_type.startsWith('audio/')) {
            type = 'audio'
          } else if (mime_type.startsWith('application/')) {
            type = 'document'
          }

          const fileData = {
            id: fileId,
            name: fileName,
            extension: extension,
            mime_type: mime_type,
            type: type,
            raw: fileItem.raw,
            size: fileItem.raw?.size || fileItem.size,
            transfer_method: 'remote_url',
            url: cloudUrl
          }
          newUploadedFiles.push(fileData)
        } catch (error) {
          this.$message.error(`文件 ${fileItem.name} 上传失败：${error.message || '未知错误'}`)
        }
      }

      // 关闭 loading
      if (this.loadings) {
        this.loadings.close()
        this.loadings = null
      }

      const tempAllFiles = [...this.allFiles, ...newUploadedFiles]
      this.allFiles = this.uniqueFiles(tempAllFiles).slice(0, this.realLimit)
      this.$emit('upload-success', [...this.allFiles])
      this.$emit('update:historyFiles', [...this.allFiles])
      if (newUploadedFiles.length > 0) {
        this.$message.success(`上传成功，${newUploadedFiles.length} 个文件`)
      }
      this.resetUploadState()
    },

    // 获取腾讯云密钥
    getTenXunCloudKey() {
      return new Promise((resolve, reject) => {
        getPolicyTxCloudAiCourse().then(res => {
          res.data ? resolve(res.data) : reject(new Error('获取腾讯云密钥失败'))
        }).catch(err => reject(err))
      })
    },

    // 初始化COS实例
    getCosInIt() {
      if (this.cosInstance) return this.cosInstance
      const cos = new COS({
        SimpleUploadMethod: 'putObject',
        getAuthorization: async (options, callback) => {
          try {
            const data = await this.getTenXunCloudKey()
            callback({
              TmpSecretId: data.tmpSecretId,
              TmpSecretKey: data.tmpSecretKey,
              SecurityToken: data.sessionToken,
              StartTime: data.startTime,
              ExpiredTime: data.expiredTime,
            })
          } catch (error) {
            callback(null, error)
          }
        }
      })
      this.cosInstance = cos
      return cos
    },

    // 腾讯云文件上传
    async TenXunCloudUploadFile(file, onProgress) {
      return new Promise((resolve, reject) => {
        this.getTenXunCloudKey().then(key => {
          const cos = this.getCosInIt()
          const fileTypeArr = file.name.split('.')
          const fileName = fileTypeArr[0]
          const extension = fileTypeArr[fileTypeArr.length - 1]

          cos.uploadFile({
            Bucket: key.bucket,
            Region: key.region,
            Key: `${key.filePath}/${fileName}.${extension}`,
            Body: file,
            SliceSize: 1024 * 1024 * 5,
            onProgress: (progressData) => {
              // 调用外部进度回调
              if (typeof onProgress === 'function') {
                onProgress(progressData.percent)
              }
            },
          }).then(data => {
            resolve({ fileName: file.name, fileurl: `${key.baseHttp}${key.filePath}/${fileName}.${extension}` })
          }).catch(err => {
            reject(new Error(`腾讯云上传失败：${err.message || '未知错误'}`))
          })
        }).catch(err => {
          reject(err)
        })
      })
    },

    // 文件超限提示
    handleExceed(files, fileList) {
      this.$message.warning(`最多只能上传 ${this.realLimit} 个文件，本次选择了 ${files.length} 个，已超出限制`);
      // 强制截取到限制数量
      this.fileList = fileList.slice(0, this.realLimit);
    },

    // 重置所有状态
    resetAllState() {
      this.allFiles = []
      this.resetUploadState()
      this.$emit('update:historyFiles', [])
    }
  },
  beforeDestroy() {
    this.loadings?.close()
  }
}
</script>

<style scoped>
.file-info ::v-deep .el-tooltip{
  height: auto !important;
}
.upload-input-hidden input[type="file"] {
  display: none;
}

.file-upload-component {
  padding: 5px 0;
  width: 100%;
}

/* 上传限制信息 */
.upload-limit-info {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 10px;
  padding: 8px 12px;
  background: #f4f4f5;
  border-radius: 4px;
}

.limit-text {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;
}

.limit-text i {
  font-size: 14px;
}

/* 已上传文件容器 */
.uploaded-files-container {
  margin-top: 10px;
  padding: 12px;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  background: #fafafa;
  min-height: 350px;
  overflow-y: auto;
}

.files-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.files-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 6px;
}

.files-title i {
  font-size: 16px;
  color: #409eff;
}

.file-group {
  margin-bottom: 12px;
}

.file-group:last-child {
  margin-bottom: 0;
}

.file-group-title {
  font-size: 13px;
  font-weight: 500;
  color: #606266;
  margin-bottom: 8px;
  padding-left: 8px;
  border-left: 3px solid #409eff;
  display: flex;
  align-items: center;
  gap: 4px;
}

.file-items {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.file-item {
  display: flex;
  flex-direction: column;
  width: 140px;
  flex-shrink: 0;
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  padding: 8px;
  background: #fff;
  transition: all 0.2s;
}

.file-item:hover {
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
}

/* 文件缩略图 */
.file-thumbnail {
  position: relative;
  width: 100%;
  height: 100px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
}

.image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.file-thumbnail:hover .file-overlay {
  opacity: 1;
}

.file-overlay i {
  font-size: 24px;
  color: #fff;
}

/* 视频样式 */
.video-thumbnail video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 视频默认封面 */
.video-cover {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.video-default-cover {
  width: 80px;
  height: 80px;
  opacity: 0.6;
}

.video-play-icon {
  position: absolute;
  font-size: 36px;
  color: #fff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.video-duration {
  position: absolute;
  bottom: 4px;
  right: 4px;
  padding: 2px 6px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 11px;
  border-radius: 2px;
}

.play-overlay i {
  font-size: 32px;
}

/* 音频样式 */
.audio-icon {
  width: 100%;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
}

.audio-icon i {
  font-size: 40px;
  color: #fff;
}

/* 文档样式 */
.document-icon {
  width: 100%;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.document-icon:hover {
  background: #ecf5ff;
}

.document-icon i {
  font-size: 48px;
}

.doc-pdf { color: #f56c6c; }
.doc-word { color: #409eff; }
.doc-excel { color: #67c23a; }
.doc-ppt { color: #e6a23c; }

/* 文件信息 */
.file-info {
  margin-top: 8px;
}

.file-name {
  font-size: 12px;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
  text-align: center;
}

.file-size {
  font-size: 11px;
  color: #909399;
  text-align: center;
  margin-bottom: 4px;
}

.file-actions {
  display: flex;
  justify-content: center;
  margin-top: 15px;
  gap: 4px;
}

.file-actions .el-button--text {
  padding: 4px;
}

.file-actions i {
  font-size: 14px;
}

/* 超出数量提示样式 */
.upload-limit-tips {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tips-text {
  font-size: 12px;
  color: #f56c6c;
}

.text-danger {
  color: #f56c6c;
}

/* 图片预览弹窗 */
.image-preview-dialog >>> .el-dialog__body {
  display: flex;
  justify-content: center;
  align-items: center;
  background: #000;
}

.preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

/* 卡片样式上传区域 */
.upload-trigger-card {
  margin-bottom: 12px;
}

.upload-card-area {
  width: 100%;
}

.upload-card-area >>> .el-upload,
.upload-card-area >>> .el-upload-dragger {
  width: 100%;
  height: auto;
  min-height: 120px;
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  background: #fafafa;
  transition: all 0.3s;
}

.upload-card-area >>> .el-upload-dragger:hover {
  border-color: #409eff;
  background: #f0f7ff;
}

.upload-card-area >>> .el-upload-dragger.is-dragover {
  border-color: #409eff;
  background: #ecf5ff;
}

.upload-card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  min-height: 72px;
}

.upload-card-icon {
  font-size: 40px;
  color: #c0c4cc;
  margin-bottom: 12px;
  transition: color 0.3s;
}

.upload-card-area >>> .el-upload-dragger:hover .upload-card-icon {
  color: #409eff;
}

.upload-card-text {
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
  font-weight: 500;
}

.upload-card-hint {
  font-size: 12px;
  color: #909399;
}

/* 上传loading遮罩层 - 高层级 */
.upload-loading-mask {
  z-index: 99999 !important;
}
</style>